import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Icon,
  IconButton,
  List,
  Menu,
  MenuItem,
  MenuItemProps,
  Typography,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import React from 'react'
import { Link } from 'react-router-dom-v5-compat'

import IconText from '@/app/component/layout/icon-text'
import { Organization, SelectOption } from '@/app/types'

import { isPageActive } from './utils'

type NavItem = {
  id: string
  icon: string
  title: string
  url: string
  displayWithoutOrg: boolean
  pageGroup: Record<string, string>
}

type Props = {
  activeOrg?: Organization
  currentPage: string
  email: string
  hasOrg: boolean
  menuOpen: boolean
  navItems: NavItem[]
  organizations: SelectOption<number>[]
  panelExpanded: boolean
  onLogoutClick: () => void
  onMenuToggle: () => void
  onOrganizationChange: (org: SelectOption<number>) => void
  onPanelToggle: () => void
}

const AccountMenu: React.FC<Props> = ({
  activeOrg,
  currentPage,
  email,
  hasOrg,
  navItems,
  menuOpen,
  organizations,
  panelExpanded,
  onLogoutClick,
  onMenuToggle,
  onOrganizationChange,
  onPanelToggle,
}) => {
  const anchorElRef = React.useRef<HTMLButtonElement | null>(null)

  const displayNavItems = React.useMemo(
    () => navItems.filter((item) => hasOrg || item.displayWithoutOrg),
    [hasOrg, navItems],
  )

  return (
    <>
      <IconButton
        data-testid="header-account-trigger"
        id="header-account-trigger"
        color="inherit"
        ref={anchorElRef}
        onClick={onMenuToggle}
      >
        <Icon>account_circle</Icon>
      </IconButton>
      <Menu id="header-account-menu" open={menuOpen} onClose={onMenuToggle} anchorEl={anchorElRef.current}>
        <DropdownItem disabled isLast>
          <IconText>
            <Icon>person</Icon>
            <Typography>{email}</Typography>
          </IconText>
        </DropdownItem>
        {displayNavItems.map(({ title, url, icon, pageGroup }, i) => (
          <DropdownItem
            component={Link}
            isLast={i + 1 === displayNavItems.length}
            key={i}
            selected={isPageActive(currentPage, pageGroup)}
            to={url}
          >
            <IconText>
              <Icon>{icon}</Icon>
              <Typography>{title}</Typography>
            </IconText>
          </DropdownItem>
        ))}
        {organizations.length > 1 && (
          <DropdownExpand>
            <DropdownExpandPanel expanded={panelExpanded} onChange={onPanelToggle}>
              <DropdownExpandPanelSummary expandIcon={<Icon>expand_more</Icon>}>
                <div>
                  <Typography variant="caption">Switch organization to:</Typography>
                  <IconText>
                    <Icon>supervised_user_circle</Icon>
                    <Typography>
                      {activeOrg?.name} ({activeOrg?.orgId})
                    </Typography>
                  </IconText>
                </div>
              </DropdownExpandPanelSummary>
              <DropdownExpandPanelDetails>
                <DropdownExpandPanelList>
                  {organizations.map(({ label, value }) => (
                    <MenuItem
                      key={value}
                      selected={value === activeOrg?.orgId}
                      disabled={value === activeOrg?.orgId}
                      onClick={() => onOrganizationChange({ label, value })}
                    >
                      <IconText>
                        <Icon />
                        <Typography>{label}</Typography>
                      </IconText>
                    </MenuItem>
                  ))}
                </DropdownExpandPanelList>
              </DropdownExpandPanelDetails>
            </DropdownExpandPanel>
          </DropdownExpand>
        )}
        <DropdownItem>
          <DropdownLink onClick={onLogoutClick}>
            <IconText>
              <DropdownIcon>exit_to_app</DropdownIcon>
              <Typography>Log out</Typography>
            </IconText>
          </DropdownLink>
        </DropdownItem>
      </Menu>
    </>
  )
}

const DropdownItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isLast',
})<
  MenuItemProps & {
    isLast?: boolean
    to?: string
  }
>(({ isLast, theme }) => ({
  ...(isLast && {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
  }),
}))

const DropdownLink = styled('div')({
  width: '100%',
  display: 'flex',
  textDecoration: 'none',
  color: 'inherit',
})

const DropdownIcon = styled(Icon)({
  marginRight: '1rem',
})

const DropdownExpand = styled(MenuItem)(({ theme }) => ({
  height: 'auto',
  minHeight: '49px',
  padding: 0,
  '&:not(:last-child)': {
    borderBottom: `1px solid ${theme.palette.grey[400]}`,
  },
}))

const DropdownExpandPanel = styled(Accordion)({
  padding: 0,
  marginBottom: '0 !important',
  width: '100%',
})

const DropdownExpandPanelSummary = styled(AccordionSummary)({
  display: 'flex',
  width: '100%',
  boxSizing: 'border-box',
  paddingLeft: '16px',
  paddingRight: '16px',
})

const DropdownExpandPanelDetails = styled(AccordionDetails)({
  padding: 0,
})

const DropdownExpandPanelList = styled(List)({
  padding: 0,
  width: '100%',
})

export default AccountMenu
